/**
 * The Filters panel (#2290): every filter as a field. A panel under the box
 * on a desktop, a bottom sheet on a phone (CSS only, so no first-frame jump).
 *
 * The panel holds no copy of the search: each field shows the query the
 * box's text makes and writes back through `onSet`, which rewrites that
 * field's tokens in the text. So the box, the chips and the panel are one
 * search. Changes apply as they are made; the results behind the panel
 * update, and its button shows how many there are.
 */
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import TagInput from '@/components/common/TagInput';
import ActorSearch from '@/components/acl/ActorSearch';
import EntityPicker from '@/components/common/EntityPicker';
import StyledSelect from '@/components/common/StyledSelect';
import { SegmentedControl } from '@/components/common/SegmentedControl';
import { searchModelTags } from '@/api/models';
import { useRowKeys } from '@/hooks/useRowKeys';
import { getLabel } from '@/utils/label';
import { QUERY_SORTS, QUERY_WITHIN, type QueryWithin } from '@/types/modelQuery';
import {
  MAX_QUERY_GROUPS,
  SORT_LABELS,
  describeGroups,
  type DateFilter,
  type EntityKind,
  type EntityLookup,
  type Field,
  type Groups,
  type SearchQuery,
} from './searchSyntax';
import './discoverSearch.css';

export interface ResultCount {
  n: number;
  more: boolean;
  searching: boolean;
}

interface Props {
  id: string;
  query: SearchQuery;
  lookup: EntityLookup;
  onSet: (field: Field, q: SearchQuery) => void;
  onLearn: (kind: EntityKind, id: string, label: string) => void;
  onClearAll: () => void;
  onClose: () => void;
  /** The field to bring into view and focus (a tapped chip's). */
  focusField: Field | null;
  count: ResultCount;
  /** Lifts the phone sheet above a bottom classification banner. */
  sheetBottom?: number;
  /** The "Save search" control, when saved searches are on (#2291). */
  saveSearch?: ReactNode;
  /** The saved searches, listed under the filters (#2291). */
  saved?: ReactNode;
}

const NONE = new Set<string>();

type Period = 'any' | QueryWithin | 'custom';
const PERIODS: { value: Period; label: string }[] = [
  { value: 'any', label: 'Any' },
  ...QUERY_WITHIN.map((w) => ({ value: w, label: w })),
  { value: 'custom', label: 'Custom' },
];

const SECTION: Record<Field, string> = {
  text: 'text', 'tags.all': 'tags-all', 'tags.any': 'tags-any', 'tags.none': 'tags-none', authors: 'authors',
  updated: 'updated', created: 'created', pick: 'pick', shared: 'shared',
  uses: 'uses', submodels: 'submodels', sort: 'sort',
};

/**
 * The functionals, or submodels, a model must use (#2299), as alternatives:
 * each group a row of chips joined by "and", the rows split by "or". A model
 * matches when it uses every one of at least one row. "+ Add" adds to a row,
 * "+ Or…" starts a new one, and a line under them says the whole filter in
 * words.
 */
function GroupsField({ field, label, kind, noun, groups, lookup, onChange, onLearn }: Readonly<{
  field: 'uses' | 'submodels';
  label: string;
  kind: 'functional' | 'model';
  /** "functional" or "submodel", for the controls' names. */
  noun: string;
  groups: Groups;
  lookup: EntityLookup;
  onChange: (groups: Groups) => void;
  onLearn: (kind: EntityKind, id: string, label: string) => void;
}>) {
  const labelId = useId();
  // A new, still empty alternative: it becomes a group with its first pick.
  const [adding, setAdding] = useState(false);
  // Row keys that travel with their alternative, so a row keeps its picker
  // (and what is typed in it) when an alternative above it is removed.
  const rowKeys = useRowKeys(groups.length);
  const nameOf = (id: string) => lookup.labelOf(kind, id) ?? id;
  const tone = field === 'uses' ? 'uses' : 'submodel';
  const set = (i: number, g: string[]) => {
    if (g.length) {
      onChange(groups.map((x, j) => (j === i ? g : x)));
    } else {
      onChange(groups.filter((_, j) => j !== i));
      rowKeys.removed(i);
    }
  };
  const addGroup = (id: string) => {
    onChange([...groups, [id]]);
    rowKeys.appended();
  };
  const picker = (placeholder: string, onPick: (id: string) => void) => (
    <div className="dsq-picker">
      <EntityPicker
        kind={kind}
        value={null}
        placeholder={placeholder}
        onChange={(r) => {
          if (!r) return;
          const id = r.id.toLowerCase();
          onLearn(kind, id, r.label);
          onPick(id);
        }}
      />
    </div>
  );
  const rows = groups.map((g, i) => (
    <fieldset key={rowKeys.keys[i]} className="dsq-group min-w-auto" aria-label={`${label}, alternative ${i + 1}`}>
      {i > 0 && <div className="dsq-or" aria-hidden="true"><span>or</span></div>}
      <div className="dsq-panel-row">
        {g.map((id, k) => (
          <span key={id} className="contents">
            {k > 0 && <span className="dsq-and" aria-hidden="true">and</span>}
            <span className={`dsq-chip dsq-k-${tone}`}>
              <span className="dsq-chip-body" style={{ cursor: 'default' }}>
                <span className="dsq-chip-value">{nameOf(id)}</span>
              </span>
              <button type="button" className="dsq-chip-x" aria-label={`Remove ${noun} ${nameOf(id)}`} onClick={() => set(i, g.filter((x) => x !== id))}>
                <FontAwesomeIcon icon={faXmark}/>
              </button>
            </span>
          </span>
        ))}
        <button type="button" className="dsq-link" aria-label={`Remove alternative ${i + 1}`} onClick={() => set(i, [])}>Remove</button>
      </div>
      {picker(`And another ${noun}…`, (id) => { if (!g.includes(id)) set(i, [...g, id]); })}
    </fieldset>
  ));
  return (
    <div className="dsq-panel-field" data-field={field}>
      <span className="dsq-panel-label" id={labelId}>{label}</span>
      {rows}
      {groups.length === 0 && picker(`Add a ${noun}…`, addGroup)}
      {groups.length > 0 && adding && (
        <fieldset className="dsq-group min-w-auto" aria-label={`${label}, new alternative`}>
          <div className="dsq-or" aria-hidden="true"><span>or</span></div>
          {picker(`Or a ${noun}…`, (id) => { setAdding(false); addGroup(id); })}
        </fieldset>
      )}
      {groups.length > 0 && !adding && groups.length < MAX_QUERY_GROUPS && (
        <button type="button" className="dsq-link self-start" onClick={() => setAdding(true)}>+ Or…</button>
      )}
      {groups.length > 0 && (
        <p className="dsq-groups-summary" aria-live="polite">Uses {describeGroups(groups, nameOf)}</p>
      )}
    </div>
  );
}

const words = (s: string) => s.trim().split(/\s+/).filter(Boolean).join(' ');

function countLabel(c: ResultCount): string {
  if (c.searching) return 'Counting…';
  if (c.n === 0) return 'No models match';
  return `Show ${c.n}${c.more ? '+' : ''} model${c.n === 1 && !c.more ? '' : 's'}`;
}

function DateField({ field, label, value, onChange }: Readonly<{
  field: 'updated' | 'created';
  label: string;
  value: DateFilter | null;
  onChange: (f: DateFilter | null) => void;
}>) {
  const labelId = useId();
  const [customOpen, setCustomOpen] = useState(false);
  let period: Period = customOpen ? 'custom' : 'any';
  if (value) period = 'within' in value ? value.within : 'custom';
  const range = value && !('within' in value) ? value : {};
  const setRange = (from: string, to: string) =>
    onChange(from || to ? { ...(from ? { from } : {}), ...(to ? { to } : {}) } : null);
  return (
    <div className="dsq-panel-field" data-field={SECTION[field]}>
      <span className="dsq-panel-label" id={labelId}>{label}</span>
      <SegmentedControl<Period>
        className="dsq-seg"
        ariaLabelledby={labelId}
        value={period}
        options={PERIODS}
        onChange={(p) => {
          setCustomOpen(p === 'custom');
          if (p === 'any') onChange(null);
          else if (p !== 'custom') onChange({ within: p });
          else if (value && 'within' in value) onChange(null);
        }}
      />
      {period === 'custom' && (
        <div className="dsq-panel-row">
          <input
            type="date"
            className="dsq-panel-input"
            style={{ flex: '1 1 130px', width: 'auto' }}
            aria-label={`${label} from`}
            value={range.from ?? ''}
            onChange={(e) => setRange(e.target.value, range.to ?? '')}
          />
          <span className="text-[12px] text-aurora-text-muted/70">to</span>
          <input
            type="date"
            className="dsq-panel-input"
            style={{ flex: '1 1 130px', width: 'auto' }}
            aria-label={`${label} to`}
            value={range.to ?? ''}
            onChange={(e) => setRange(range.from ?? '', e.target.value)}
          />
        </div>
      )}
    </div>
  );
}

export default function FiltersPanel({
  id, query, lookup, onSet, onLearn, onClearAll, onClose, focusField, count, sheetBottom = 0, saveSearch, saved,
}: Readonly<Props>) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const set = (field: Field, patch: Partial<SearchQuery>) => onSet(field, { ...query, ...patch });
  const setTags = (field: 'tags.all' | 'tags.any' | 'tags.none', tags: string[]) =>
    set(field, { tags: { ...query.tags, [field.slice(5)]: tags } });

  // The text field keeps its own draft, so a trailing space survives while
  // typing; it follows the box whenever the box's words change.
  const [draft, setDraft] = useState(query.text);
  const [seenText, setSeenText] = useState(query.text);
  if (seenText !== query.text) {
    setSeenText(query.text);
    if (words(draft) !== query.text) setDraft(query.text);
  }

  const [groupMode, setGroupMode] = useState(false);
  let sharedMode: 'me' | 'group' | 'any' = 'any';
  if (query.shared === 'me') sharedMode = 'me';
  else if (query.shared || groupMode) sharedMode = 'group';

  // A desktop panel hangs from the box's right edge; when the box sits at the
  // left of a narrower page that would put the panel's left part off screen
  // (1280 px since #2292's Customize button), so keep it inside the window.
  useLayoutEffect(() => {
    const el = ref.current;
    const parent = el?.offsetParent;
    if (!el || !parent || getComputedStyle(el).position !== 'absolute') return;
    const r = el.getBoundingClientRect();
    const left = Math.min(Math.max(r.left, 8), window.innerWidth - r.width - 8);
    if (Math.abs(left - r.left) < 1) return;
    el.style.right = 'auto';
    el.style.left = `${left - parent.getBoundingClientRect().left}px`;
  }, []);

  // Bring the asked-for field into view and focus it.
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const section = focusField ? root.querySelector<HTMLElement>(`[data-field="${SECTION[focusField]}"]`) : null;
    const target = (section ?? root).querySelector<HTMLElement>('input, button, [tabindex="0"]');
    section?.scrollIntoView?.({ block: 'nearest' });
    target?.focus();
    if (!section) return;
    section.classList.add('is-focus');
    const t = setTimeout(() => section.classList.remove('is-focus'), 1200);
    return () => clearTimeout(t);
  }, [focusField]);

  const people = useMemo(() => query.authors.filter((a) => a !== 'me'), [query.authors]);

  const sortOptions = [
    { value: '', label: 'Default: best match, else recently updated' },
    ...QUERY_SORTS.map((s) => ({ value: s, label: SORT_LABELS[s] })),
  ];

  return (
    <>
      <div className="dsq-panel-backdrop" onClick={onClose} aria-hidden="true"/>
      <div // NOSONAR typescript:S6847 accessibility-by-design: mouse-only backdrop/handler, keyboard dismiss is the Escape handler and close button
        ref={ref}
        id={id}
        className="dsq-panel"
        role="dialog"
        aria-labelledby={titleId}
        style={{ ['--dsq-sheet-bottom' as string]: `${sheetBottom}px` }}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !e.defaultPrevented) { e.stopPropagation(); onClose(); }
        }}
      >
        <div className="dsq-grab" aria-hidden="true"/>
        <div className="dsq-panel-head">
          <h2 className="dsq-panel-title" id={titleId}>Filters</h2>
          <button type="button" className="dsq-iconbtn" aria-label="Close filters" onClick={onClose}>
            <FontAwesomeIcon icon={faXmark}/>
          </button>
        </div>

        <div className="dsq-panel-grid">
          <label className="dsq-panel-field is-full" data-field="text">
            <span className="dsq-panel-label">Text</span>
            <input
              className="dsq-panel-input"
              value={draft}
              placeholder="Words in the name, description or tags"
              onChange={(e) => { setDraft(e.target.value); set('text', { text: words(e.target.value) }); }}
            />
          </label>

          <div className="dsq-panel-field" data-field="tags-all">
            <span className="dsq-panel-label">Has all of these tags</span>
            <TagInput value={query.tags.all} onChange={(t) => setTags('tags.all', t)} searchTags={searchModelTags} placeholder="Add a tag…"/>
          </div>
          <div className="dsq-panel-field" data-field="tags-any">
            <span className="dsq-panel-label">Has any of these tags</span>
            <TagInput value={query.tags.any} onChange={(t) => setTags('tags.any', t)} searchTags={searchModelTags} placeholder="Add a tag…"/>
          </div>
          <div className="dsq-panel-field" data-field="tags-none">
            <span className="dsq-panel-label">Exclude tags</span>
            <TagInput value={query.tags.none} onChange={(t) => setTags('tags.none', t)} searchTags={searchModelTags} placeholder="Add a tag to exclude…"/>
          </div>

          <div className="dsq-panel-field" data-field="authors">
            <span className="dsq-panel-label">Author</span>
            <div className="dsq-panel-row">
              <button
                type="button"
                className="dsq-toggle"
                aria-pressed={query.authors.includes('me')}
                onClick={() => set('authors', {
                  authors: query.authors.includes('me') ? people : ['me', ...people],
                })}
              >
                Me
              </button>
              {people.map((a) => (
                <span key={a} className="dsq-chip dsq-k-author">
                  <span className="dsq-chip-body" style={{ cursor: 'default' }}>
                    <span className="dsq-chip-value">{lookup.labelOf('author', a) ?? a}</span>
                  </span>
                  <button
                    type="button"
                    className="dsq-chip-x"
                    aria-label={`Remove author ${lookup.labelOf('author', a) ?? a}`}
                    onClick={() => set('authors', { authors: query.authors.filter((x) => x !== a) })}
                  >
                    <FontAwesomeIcon icon={faXmark}/>
                  </button>
                </span>
              ))}
            </div>
            <ActorSearch
              includeGroups={false}
              excludeRefs={NONE}
              label=""
              placeholder="Add a person…"
              onPick={(actor) => {
                const pid = actor._id.toLowerCase();
                onLearn('author', pid, getLabel(actor.metadata.label) || pid);
                if (!query.authors.includes(pid)) set('authors', { authors: [...query.authors, pid] });
              }}
            />
          </div>

          <GroupsField
            field="uses"
            label="Uses functionals"
            kind="functional"
            noun="functional"
            groups={query.functionals}
            lookup={lookup}
            onChange={(g) => set('uses', { functionals: g })}
            onLearn={onLearn}
          />
          <GroupsField
            field="submodels"
            label="Uses submodels"
            kind="model"
            noun="submodel"
            groups={query.submodels}
            lookup={lookup}
            onChange={(g) => set('submodels', { submodels: g })}
            onLearn={onLearn}
          />

          <DateField field="updated" label="Updated" value={query.updated} onChange={(f) => set('updated', { updated: f })}/>
          <DateField field="created" label="Created" value={query.created} onChange={(f) => set('created', { created: f })}/>

          <div className="dsq-panel-field" data-field="pick">
            <span className="dsq-panel-label">Editor's picks</span>
            <div className="dsq-switch-row">
              <span>Editor's picks only</span>
              <button
                type="button"
                role="switch"
                className="dsq-toggle"
                aria-checked={query.editorsPick}
                aria-label="Editor's picks only"
                onClick={() => set('pick', { editorsPick: !query.editorsPick })}
              >
                {query.editorsPick ? 'On' : 'Off'}
              </button>
            </div>
          </div>

          <div className="dsq-panel-field" data-field="shared">
            <span className="dsq-panel-label">Shared</span>
            <SegmentedControl<'any' | 'me' | 'group'>
              className="dsq-seg"
              ariaLabel="Shared"
              value={sharedMode}
              options={[{ value: 'any', label: 'Any' }, { value: 'me', label: 'With me' }, { value: 'group', label: 'With a group' }]}
              onChange={(m) => {
                setGroupMode(m === 'group');
                if (m === 'any') set('shared', { shared: null });
                else if (m === 'me') set('shared', { shared: 'me' });
                else if (query.shared === 'me') set('shared', { shared: null });
              }}
            />
            {sharedMode === 'group' && (query.shared && query.shared !== 'me' ? (
              <span className="dsq-chip dsq-k-shared self-start">
                <span className="dsq-chip-body" style={{ cursor: 'default' }}>
                  <span className="dsq-chip-value">{lookup.labelOf('group', query.shared) ?? query.shared}</span>
                </span>
                <button type="button" className="dsq-chip-x" aria-label="Remove group" onClick={() => set('shared', { shared: null })}>
                  <FontAwesomeIcon icon={faXmark}/>
                </button>
              </span>
            ) : (
              <ActorSearch
                includeUsers={false}
                excludeRefs={NONE}
                label=""
                placeholder="Pick a group…"
                onPick={(actor) => {
                  const gid = actor._id.toLowerCase();
                  onLearn('group', gid, getLabel(actor.metadata.label) || gid);
                  set('shared', { shared: gid });
                }}
              />
            ))}
          </div>

          <div className="dsq-panel-field" data-field="sort">
            <span className="dsq-panel-label">Sort</span>
            <StyledSelect
              className="dsq-panel-input"
              aria-label="Sort"
              value={query.sort ?? ''}
              options={sortOptions}
              onChange={(v) => set('sort', { sort: (v || null) as SearchQuery['sort'] })}
            />
          </div>

          {saved}
        </div>

        <div className="dsq-panel-foot">
          <div className="flex items-center gap-2">
            <button type="button" className="dsq-link" onClick={onClearAll}>Clear all</button>
            {saveSearch}
          </div>
          <button type="button" className="dsq-apply" onClick={onClose} aria-live="polite">
            {countLabel(count)}
          </button>
        </div>
      </div>
    </>
  );
}
